/**
 * Single entry point for PostHog on web. No other file should import
 * `posthog-js` directly or call `posthog.capture(...)` — go through
 * `analytics.track(...)` instead.
 *
 * Mirrors `services/analyticsService.ts` in the NameIt React Native app
 * (react-native-projects/NameItApp): same exported shape (`init`,
 * `identify`, `track`, `screen`, `reset`), same super-property
 * registration on init, same reasoning for manual pageview/screen
 * tracking. `page` is an additional alias for `screen` — see below — and
 * `track`'s generic is narrowed to `ClientEventName` rather than the full
 * `AnalyticsEventName`, a web-side hardening of the money-events-are-
 * server-side rule mobile currently only documents (see analyticsEvents.ts).
 */
import posthog from "posthog-js";
import type { AnalyticsEventMap, ClientEventName } from "./analyticsEvents";

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ||
  "https://us.i.posthog.com";

// One of 'development' | 'preview' | 'production'. Prefers an explicit
// VITE_APP_ENV (set per Vercel environment scope, the same way mobile sets
// EXPO_PUBLIC_APP_ENV per build profile) and falls back to Vercel's own
// VERCEL_ENV (inlined at build time via vite.config.js's `define`, since
// Vercel sets it automatically with no per-project config needed) before
// defaulting to 'development' — so a build that configured neither is never
// silently counted as production traffic.
const APP_ENVIRONMENT: "development" | "preview" | "production" = (() => {
  const explicit = import.meta.env.VITE_APP_ENV as string | undefined;
  const fromBuild = typeof __APP_ENVIRONMENT__ !== "undefined" ? __APP_ENVIRONMENT__ : undefined;
  const raw = explicit || fromBuild || "development";
  return raw === "production" || raw === "preview" ? raw : "development";
})();

// In local development, events are still tagged and sent — useful for
// verifying instrumentation end-to-end. Filter `environment != 'development'`
// on dashboards rather than dropping dev events client-side, so debugging
// stays possible.
const IS_DEV_ENVIRONMENT = APP_ENVIRONMENT === "development";

let initialized = false;

function init(): void {
  if (initialized) return;
  initialized = true;

  if (!POSTHOG_KEY) {
    console.warn("[analytics] VITE_POSTHOG_KEY is not set — analytics will be a no-op.");
    return;
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,

    // We track pageviews manually (see screen()/page() below) rather than
    // trust autocapture for this — confirmed on the sibling React Native
    // app that autocapture never produced screen-view events for its
    // router (zero $screen events in live PostHog data despite autocapture
    // being on). Do the same live-data check here once this project has
    // real traffic; if capture_pageview turns out to work fine on this
    // router, remove ONE of the two — never leave both, that double-counts.
    capture_pageview: false,
    capture_pageleave: true,
    // Click/interaction autocapture stays on; only pageview capture is
    // replaced by the manual call above.
    autocapture: true,

    // No Sentry yet — PostHog's own exception capture is the only crash
    // channel this app has. Hooks window.onerror/onunhandledrejection
    // automatically; mirrors the manual ErrorUtils/rejection-tracking
    // wiring on the mobile side, which has no equivalent built-in flag.
    capture_exceptions: true,

    session_recording: {
      // Mask every free-text input value (chat, names, category answers —
      // anything typed), matching mobile's `maskAllTextInputs: true`. This
      // app's toggles are plain non-input divs, not native checkboxes, so
      // there's nothing non-text to leave deliberately unmasked beyond the
      // range/color types rrweb itself never treats as free text.
      maskAllInputs: true,
      maskInputOptions: {
        text: true,
        textarea: true,
        email: true,
        password: true,
        tel: true,
        search: true,
        number: true,
        // Not free text — no reason to mask these.
        range: false,
        color: false,
      },
      // Escape hatch for free-text surfaces that aren't real <input>/
      // <textarea> elements (e.g. a contenteditable chat box) — add
      // data-ph-mask to those elements.
      maskTextSelector: "[data-ph-mask]",
      // Fully block the Paystack checkout surface from the recording,
      // matching mobile's `maskAllSandboxedViews: true` (which fully masks
      // the Paystack WebView there). Matches by iframe src rather than a
      // container id/class we control, since @paystack/inline-js injects
      // its iframe directly onto document.body outside our React tree —
      // verify this selector still matches once real payments run under
      // session replay, Paystack's inline widget markup isn't a documented
      // contract and can change between SDK versions.
      blockSelector: 'iframe[src*="paystack"]',
    },
  });

  posthog.register({
    environment: APP_ENVIRONMENT,
    platform: "web",
    app_version: typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "0.0.0",
    git_commit: typeof __APP_GIT_COMMIT__ !== "undefined" ? __APP_GIT_COMMIT__ : "local",
    deploy_id: typeof __APP_DEPLOY_ID__ !== "undefined" ? __APP_DEPLOY_ID__ : "local",
  });

  if (IS_DEV_ENVIRONMENT) {
    console.log(`[analytics] initialized (environment=${APP_ENVIRONMENT})`);
  }
}

/**
 * Associate all future events with a known user. Pass the same id used as
 * the Firebase Auth UID — the server-side capture (Paystack webhook) uses
 * the same UID as its distinct_id, so client and server events merge into
 * one PostHog person.
 */
function identify(userId: string, traits?: Record<string, unknown>): void {
  if (!initialized) return;
  posthog.identify(userId, traits);
}

/**
 * Type-checked event capture. `event` must be a key of AnalyticsEventMap
 * and `properties` must match that event's declared shape — a typo in the
 * event name or a missing/wrong property is a TypeScript error here, not a
 * silent gap in a dashboard. Only accepts ClientEventName
 * (AnalyticsEventMap minus the server-only money/outcome events); those are
 * captured from the Paystack webhook instead (functions/index.js).
 */
function track<E extends ClientEventName>(event: E, properties: AnalyticsEventMap[E]): void {
  if (!initialized) return;
  posthog.capture(event, properties);
}

/**
 * Manual pageview/screen tracking, called on every route change (see
 * useAnalyticsPageviews.js, wired into App.jsx). Named `screen` to match
 * the mobile app's method name 1:1 so both platforms' pageviews land under
 * the same `$screen` event instead of two differently-named ones to
 * reconcile by hand; `page` is a web-idiomatic alias for the same call.
 */
function screen(name: string, properties?: Record<string, unknown>): void {
  if (!initialized) return;
  posthog.capture("$screen", { $screen_name: name, ...properties });
}

function page(name: string, properties?: Record<string, unknown>): void {
  screen(name, properties);
}

/**
 * Call on logout so the next identify() doesn't inherit the previous
 * user's history.
 */
function reset(): void {
  if (!initialized) return;
  posthog.reset();
}

export const analytics = { init, identify, track, screen, page, reset };
export default analytics;
